//#region src/connectors/registry.ts
var ConnectorRegistry = class {
	factories = /* @__PURE__ */ new Map();
	registerConnector(factory) {
		const connectorType = factory.getProviderId();
		if (!this.factories.has(connectorType)) this.factories.set(connectorType, factory);
	}
	createConnector(providerId, config = {}) {
		const factory = this.factories.get(providerId);
		if (!factory) throw new Error(`No connector registered for provider: ${providerId}`);
		return factory.create(config);
	}
	getConnector(providerId, config = {}) {
		return this.createConnector(providerId, config);
	}
	hasConnector(providerId) {
		return this.factories.has(providerId);
	}
	getAvailableProviders() {
		return Array.from(this.factories.keys());
	}
	unregisterConnector(providerId) {
		return this.factories.delete(providerId);
	}
	clear() {
		this.factories.clear();
	}
	async getAllModels() {
		const results = [];
		for (const [providerId, factory] of this.factories) try {
			const models = await factory.create({}).listModels();
			results.push({
				provider: providerId,
				models
			});
		} catch (error) {
			console.warn(`Failed to get models from ${providerId}:`, error);
		}
		return results;
	}
};
const globalConnectorRegistry = new ConnectorRegistry();

//#endregion
//#region src/errors.ts
/**
* Custom error classes for the router package
* Following TypeScript best practices for error handling
*/
/**
* Base error class for all router-related errors
*/
var RouterError = class extends Error {
	constructor(message, code) {
		super(message);
		this.code = code;
		this.name = "RouterError";
		if (Error.captureStackTrace) Error.captureStackTrace(this, this.constructor);
	}
};
/**
* Error thrown when a connector is not found for a provider
*/
var ConnectorNotFoundError = class extends RouterError {
	constructor(providerId) {
		super(`Connector not found for provider: ${providerId}`, "CONNECTOR_NOT_FOUND");
		this.providerId = providerId;
		this.name = "ConnectorNotFoundError";
	}
};
/**
* Error thrown when a provider is not configured
*/
var ProviderNotConfiguredError = class extends RouterError {
	constructor(providerId) {
		super(`Provider '${providerId}' is not configured`, "PROVIDER_NOT_CONFIGURED");
		this.providerId = providerId;
		this.name = "ProviderNotConfiguredError";
	}
};
/**
* Error thrown when request validation fails
*/
var ValidationError = class extends RouterError {
	constructor(message, field) {
		super(message, "VALIDATION_ERROR");
		this.field = field;
		this.name = "ValidationError";
	}
};
/**
* Error thrown when a connector operation fails
*/
var ConnectorError = class extends RouterError {
	constructor(message, providerId, originalError) {
		super(message, "CONNECTOR_ERROR");
		this.providerId = providerId;
		this.originalError = originalError;
		this.name = "ConnectorError";
		if (originalError?.stack) this.stack = `${this.stack}\nCaused by: ${originalError.stack}`;
	}
};
/**
* Error thrown when a model is not supported
*/
var ModelNotSupportedError = class extends RouterError {
	constructor(modelId, providerId) {
		super(`Model '${modelId}' is not supported by provider '${providerId}'`, "MODEL_NOT_SUPPORTED");
		this.modelId = modelId;
		this.providerId = providerId;
		this.name = "ModelNotSupportedError";
	}
};
/**
* Error thrown when a timeout occurs
*/
var TimeoutError = class extends RouterError {
	constructor(operation, timeoutMs) {
		super(`Operation '${operation}' timed out after ${timeoutMs}ms`, "TIMEOUT");
		this.operation = operation;
		this.timeoutMs = timeoutMs;
		this.name = "TimeoutError";
	}
};
/**
* Error thrown when max retries are exceeded
*/
var MaxRetriesExceededError = class extends RouterError {
	constructor(providerId, operation, maxRetries, originalError) {
		super(`Max retries (${maxRetries}) exceeded for ${operation} on provider ${providerId}: ${originalError.message}`, "MAX_RETRIES_EXCEEDED");
		this.providerId = providerId;
		this.operation = operation;
		this.maxRetries = maxRetries;
		this.originalError = originalError;
		this.name = "MaxRetriesExceededError";
		if (originalError?.stack) this.stack = `${this.stack}\nCaused by: ${originalError.stack}`;
	}
};

//#endregion
//#region src/error-handler.ts
/**
* ErrorHandler manages retry logic with exponential backoff
*/
var ErrorHandler = class {
	config;
	constructor(config) {
		this.config = {
			maxRetries: config.maxRetries,
			backoffType: config.backoffType,
			baseDelay: config.baseDelay,
			maxDelay: config.maxDelay ?? 3e4,
			jitter: config.jitter ?? true
		};
	}
	/**
	* Check if an error is retryable
	*/
	isRetryable(error) {
		const status = error.status || error.statusCode;
		if (status !== void 0) {
			if (status === 429 || status >= 500 && status < 600) return true;
			if (status >= 400 && status < 500) return false;
		}
		const code = error.code;
		if (code) {
			if ([
				"ECONNRESET",
				"ECONNREFUSED",
				"ETIMEDOUT",
				"ENOTFOUND",
				"ENETUNREACH",
				"EAI_AGAIN"
			].includes(code)) return true;
		}
		if (error.name === "TimeoutError" || error.message.includes("timeout")) return true;
		return false;
	}
	/**
	* Calculate delay for retry attempt with backoff
	*/
	calculateDelay(attempt) {
		let delay;
		if (this.config.backoffType === "exponential") delay = this.config.baseDelay * Math.pow(2, attempt);
		else delay = this.config.baseDelay * (attempt + 1);
		delay = Math.min(delay, this.config.maxDelay);
		if (this.config.jitter) delay += Math.random() * 500;
		return Math.floor(delay);
	}
	/**
	* Decide whether to retry based on context
	*/
	shouldRetry(context) {
		if (context.attempt >= this.config.maxRetries) return {
			shouldRetry: false,
			reason: `Max retries (${this.config.maxRetries}) exceeded`
		};
		if (!this.isRetryable(context.error)) return {
			shouldRetry: false,
			reason: `Error is not retryable: ${context.error.message}`
		};
		const delay = this.calculateDelay(context.attempt);
		return {
			shouldRetry: true,
			delay,
			nextAttempt: context.attempt + 1,
			reason: `Error is retryable, will retry after ${delay}ms`
		};
	}
	/**
	* Execute an operation with automatic retry logic
	*/
	async executeWithRetry(operation, context) {
		let attempt = 0;
		while (true) try {
			return await operation();
		} catch (error) {
			const errorContext = {
				...context,
				error,
				attempt
			};
			const decision = this.shouldRetry(errorContext);
			if (!decision.shouldRetry) {
				if (errorContext.attempt >= this.config.maxRetries) throw new MaxRetriesExceededError(context.providerId, context.operation, this.config.maxRetries, error);
				throw error;
			}
			if (decision.delay) await this.sleep(decision.delay);
			attempt = decision.nextAttempt;
		}
	}
	/**
	* Get current retry configuration
	*/
	getRetryConfig() {
		return { ...this.config };
	}
	/**
	* Sleep for specified milliseconds
	*/
	sleep(ms) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
};
/**
* Create a default ErrorHandler instance
*/
function createErrorHandler(config) {
	return new ErrorHandler({
		maxRetries: config?.maxRetries ?? 3,
		backoffType: config?.backoffType ?? "exponential",
		baseDelay: config?.baseDelay ?? 1e3,
		maxDelay: config?.maxDelay,
		jitter: config?.jitter
	});
}

//#endregion
//#region src/lib/router.ts
var GenAIRouter = class {
	config;
	registry;
	errorHandler;
	constructor(config) {
		this.config = config;
		this.registry = new ConnectorRegistry();
		this.errorHandler = createErrorHandler({
			maxRetries: config.maxRetries ?? 3,
			backoffType: "exponential",
			baseDelay: 1e3,
			jitter: true
		});
	}
	async chatCompletion(request) {
		const { config: normalizedConfig, type } = this.normalizeProviderConfig(request.provider);
		const connector = this.getConnector(type, normalizedConfig);
		const baseRequest = {
			messages: request.messages,
			model: request.model,
			temperature: request.temperature,
			max_tokens: request.max_tokens,
			useLegacyCompletionAPI: request.useLegacyCompletionAPI,
			fallbackToChatCompletion: request.fallbackToChatCompletion,
			useLegacyMaxTokens: request.useLegacyMaxTokens,
			top_p: request.top_p,
			frequency_penalty: request.frequency_penalty,
			presence_penalty: request.presence_penalty,
			reasoning: request.reasoning,
			extra_body: request.extra_body,
			tools: request.tools,
			tool_executor: request.tool_executor
		};
		const response = await this.errorHandler.executeWithRetry(() => connector.chatCompletion(baseRequest), {
			providerId: request.provider,
			operation: "chatCompletion",
			metadata: { model: request.model }
		});
		return {
			id: response.id,
			object: response.object,
			created: response.created,
			model: response.model,
			provider: connector.getProviderId(),
			choices: response.choices.map((choice) => ({
				index: choice.index,
				message: {
					role: choice.message.role,
					content: choice.message.content,
					...choice.message.tool_calls && { tool_calls: choice.message.tool_calls }
				},
				finish_reason: choice.finish_reason
			})),
			usage: {
				prompt_tokens: response.usage.prompt_tokens,
				completion_tokens: response.usage.completion_tokens,
				total_tokens: response.usage.total_tokens
			}
		};
	}
	async response(request) {
		const { config: normalizedConfig, type } = this.normalizeProviderConfig(request.provider);
		const connector = this.getConnector(type, normalizedConfig);
		return {
			...await this.errorHandler.executeWithRetry(() => connector.response(request), {
				providerId: request.provider,
				operation: "response",
				metadata: { model: request.model }
			}),
			provider: connector.getProviderId()
		};
	}
	async listModels(provider) {
		const { config: normalizedConfig, type } = this.normalizeProviderConfig(provider);
		const connector = this.getConnector(type, normalizedConfig);
		return await this.errorHandler.executeWithRetry(() => connector.listModels(), {
			providerId: provider,
			operation: "listModels"
		});
	}
	normalizeProviderConfig(provider) {
		if (!this.config.providers?.[provider]) throw new Error(`Provider '${provider}' not configured`);
		const providerConfig = this.config.providers[provider];
		return {
			config: {
				baseURL: providerConfig.api.url,
				apiKey: providerConfig.api.token,
				headers: providerConfig.api.headers,
				timeout: providerConfig.timeout || this.config.timeout,
				maxRetries: providerConfig.maxRetries || this.config.maxRetries
			},
			type: providerConfig.type
		};
	}
	getConnector(providerId, config) {
		if (!this.registry.hasConnector(providerId)) throw new Error(`No connector registered for provider: ${providerId}`);
		return this.registry.getConnector(providerId, config);
	}
	registerConnector(factory) {
		this.registry.registerConnector(factory);
	}
	getAvailableProviders() {
		return this.registry.getAvailableProviders();
	}
	hasProvider(providerId) {
		return this.registry.hasConnector(providerId);
	}
};
function createRouter(config = {}) {
	return new GenAIRouter(config);
}
function createGateway(config = {}) {
	return new GenAIRouter(config);
}
var router_default = GenAIRouter;

//#endregion
//#region src/connectors/base/index.ts
var NoOpLogger = class {
	debug() {}
	info() {}
	warn() {}
	error() {}
};
var BaseConnector = class {
	config;
	userConfig;
	logger;
	providerId;
	static packageName;
	constructor(providerId, config = {}) {
		this.providerId = providerId;
		this.userConfig = { ...config };
		this.config = {
			timeout: 3e4,
			maxRetries: 3,
			...config
		};
		this.logger = config.logger || new NoOpLogger();
	}
	getUserConfig() {
		return { ...this.userConfig };
	}
	getEffectiveConfig() {
		return { ...this.config };
	}
	validateRequest(request) {
		const validated = { ...request };
		if (validated.temperature !== void 0) validated.temperature = Math.max(0, Math.min(2, validated.temperature));
		if (validated.top_p !== void 0) validated.top_p = Math.max(0, Math.min(1, validated.top_p));
		if (validated.frequency_penalty !== void 0) validated.frequency_penalty = Math.max(-2, Math.min(2, validated.frequency_penalty));
		if (validated.presence_penalty !== void 0) validated.presence_penalty = Math.max(-2, Math.min(2, validated.presence_penalty));
		return validated;
	}
	isInitialized() {
		return true;
	}
	getProviderId() {
		return this.providerId;
	}
	getConfig() {
		return { ...this.config };
	}
	handleError(error, operation) {
		if (error instanceof Error) throw new Error(`${this.providerId} ${operation} failed: ${error.message}`);
		throw new Error(`${this.providerId} ${operation} failed: Unknown error`);
	}
	sleep(ms) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
};

//#endregion
//#region src/config.ts
/**
* Helper function to define a router configuration with full TypeScript support
*/
function defineConfig(config) {
	return config;
}

//#endregion
export { BaseConnector, ConnectorError, ConnectorNotFoundError, ConnectorRegistry, ErrorHandler, router_default as GenAIGateway, router_default as GenAIRouter, router_default as default, MaxRetriesExceededError, ModelNotSupportedError, ProviderNotConfiguredError, RouterError, TimeoutError, ValidationError, createErrorHandler, createGateway, createRouter, defineConfig };
//# sourceMappingURL=index.js.map